import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Autocomplete,
  Grid,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { leadsApi } from '../../../services/api';
import { CreateAppointmentPayload, UpdateAppointmentPayload, RBCEvent, AppointmentSource } from '../types';
import { useBookingContext } from '../../../contexts/BookingContext';
import { formatISOWithTimezone, formatDateToDateTimeLocal, parseSlotAsWallClock } from '../../BookingConfiguration/utils';
import { MuiTelInput } from 'mui-tel-input';
import { NativePickerField } from '../../BookingConfiguration';
import { useLeads } from '../../../hooks/leads/useLeads';
import { useEditLeadDialog } from '../../../hooks/leads/useEditLeadDialog';
import { LeadSource } from '../../../types/lead';
import {
  buildSlotRangeFromDateAndTimes,
} from '../utils/slotAvailability';
import { GeneratedSlot, buildGeneratedSlotLookup, getGeneratedSlotKey } from '../utils/generatedAvailability';
import { useAvailableSlots } from '../hooks/useAvailableSlots';
import { useSlotCache } from '../context/SlotCacheContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { LeadStatusSelect } from '../../../components/leads/status';
import { DynamicFormFields } from '../../../components/dynamicFields/DynamicFormFields';
import { useCustomColumns } from '../../../hooks/leads/useCustomColumns';

type StartTimeOption = {
  time: string;
  value: string;
  booked: boolean;
  isPast: boolean;
  isPreviousDay: boolean;
};

interface AppointmentModalProps {
  open: boolean;
  onClose: () => void;
  smbId: string;
  selectedEvent: RBCEvent | null;
  onSaveCreate: (payload: CreateAppointmentPayload) => Promise<boolean>;
  onSaveUpdate: (id: string, payload: UpdateAppointmentPayload) => Promise<boolean>;
  getNow?: () => Date;
}

export const AppointmentModal: React.FC<AppointmentModalProps> = ({
  open,
  onClose,
  smbId,
  selectedEvent,
  onSaveCreate,
  onSaveUpdate,
  getNow,
}) => {
  const isEditing = !!(selectedEvent && selectedEvent.resource?.id);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState<any[]>([]);
  const [searchingLeads, setSearchingLeads] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { config } = useBookingContext();
  const { showNotification } = useNotification();
  const timezone = config?.timezone || 'UTC';

  // Cached slots from CalendarView context (pre-fetched for the visible date range)
  const { getSlotsForDate, isDateInCache } = useSlotCache();

  // Fallback local slots for dates outside the cached range
  const { availableSlots: localSlots, fetchAvailableSlots: fetchLocalSlots } = useAvailableSlots(smbId, timezone);

  const getOriginalAppointmentEnd = useCallback((): Date | null => {
    const resourceEnd = selectedEvent?.resource?.slot_end;
    if (resourceEnd) {
      return parseSlotAsWallClock(resourceEnd) ?? new Date(resourceEnd);
    }

    return selectedEvent?.end ?? null;
  }, [selectedEvent]);

  // Check if the appointment is in the past (end time before now)
  const isPastAppointment = useMemo(() => {
    if (!isEditing) return false;
    const now = getNow ? getNow() : new Date();
    const originalEnd = getOriginalAppointmentEnd();
    return !!originalEnd && originalEnd < now;
  }, [isEditing, getNow, getOriginalAppointmentEnd]);

  const { customColumns, addOptionToColumn } = useCustomColumns(smbId);

  const [isCreatingInline, setIsCreatingInline] = useState(false);
  const [newLeadName, setNewLeadName] = useState('');
  const [newLeadPhone, setNewLeadPhone] = useState('');
  const [newLeadStatus, setNewLeadStatus] = useState('New');
  const [newLeadNotes, setNewLeadNotes] = useState('');
  const [newLeadCustomFields, setNewLeadCustomFields] = useState<Record<string, any>>({});

  const handleLeadCreated = useCallback((newLead: any) => {
    const processedLead = {
      ...newLead,
      full_name: newLead.name || newLead.full_name,
      status: newLead.lead_status
    };
    // Prepend to local list so it appears in future searches
    setLeads(prev => [processedLead, ...prev.filter(l => l.id !== processedLead.id)]);
    setLeadId(processedLead.id);
    setSelectedLead(processedLead);
    setIsCreatingInline(false);
    setNewLeadName('');
    setNewLeadPhone('');
    setNewLeadStatus('New');
    setNewLeadNotes('');
    setNewLeadCustomFields({});
  }, []);

  const {
    createLead,
    isLoading: isCreatingLeadLoading,
    nameError,
    phoneError,
    serverError: inlineServerError,
    setNameError,
    setPhoneError,
    setServerError: setInlineServerError,
  } = useLeads({
    smbId,
    onSuccess: handleLeadCreated
  });
  const { openEditLead, renderEditLeadDialog } = useEditLeadDialog({
    onSuccess: (updatedLead) => {
      if (updatedLead) {
        handleLeadCreated(updatedLead);
      }
    },
    smbId,
    customColumns,
  });

  // Saves the lead that was selected before the user opened the inline form
  const prevSelectedLeadRef = useRef<any>(null);
  const leadSearchInputRef = useRef('');
  const lastSearchQueryRef = useRef('');

  // Form State
  const [leadId, setLeadId] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<any | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [services, setServices] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [source, setSource] = useState<AppointmentSource>(AppointmentSource.DIRECT);
  const [isFormEditable, setIsFormEditable] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState<{ open: boolean; type: 'UPDATE' | 'CANCEL' } | null>(null);


  useEffect(() => {
    if (open && selectedEvent) {
      const pStart = selectedEvent.start ? formatDateToDateTimeLocal(selectedEvent.start) : '';
      const pEnd = selectedEvent.end ? formatDateToDateTimeLocal(selectedEvent.end) : '';

      if (pStart) {
        const [d] = pStart.split('T');
        setSelectedDate(selectedEvent.modalDate || d);
        // Store the timestamp as the unique value for startTime
        if (selectedEvent.start) {
          setStartTime(selectedEvent.start.getTime().toString());
        }
      }
      if (pEnd) {
        setEndTime(pEnd.split('T')[1] || '');
      }

      if (isEditing && selectedEvent.resource) {
        const res = selectedEvent.resource;
        setNotes(res.notes || '');
        setServices(res.services?.[0] || '');
        setLeadId(res.lead_id);

        if (res.lead_name && res.lead_id) {
          const l = { id: res.lead_id, full_name: res.lead_name, phone: res.lead_phone };
          setLeads([l]);
          setSelectedLead(l);
        }
        setSource(res.source || AppointmentSource.DIRECT);

        // Auto-enable edit mode when the modal was opened with pre-filled times
        const injectedStart = selectedEvent.start ? formatDateToDateTimeLocal(selectedEvent.start) : '';
        const dbOriginalStart = res.slot_start
          ? formatDateToDateTimeLocal(parseSlotAsWallClock(res.slot_start) ?? new Date(res.slot_start))
          : '';
        const isPrefilled = injectedStart !== dbOriginalStart;
        setIsFormEditable(isPrefilled);
      } else {
        // Reset create form
        setLeadId(null);
        setSelectedLead(null);
        setNotes('');
        setServices('');
        setSource(AppointmentSource.DIRECT);
        setLeads([]);
        setIsFormEditable(true);
        // Reset inline create state
        setIsCreatingInline(false);
        setNewLeadName('');
        setNewLeadPhone('');
        setNewLeadNotes('');
        setNewLeadCustomFields({});
        setPhoneError(null);
        setNameError(null);
        setInlineServerError(null);
        setNewLeadStatus('New');
        // Initial fetch for new appointment to show top leads
        searchLeads('');
      }
    }
  }, [open, selectedEvent, isEditing]);

  // A "date-level" open is Month / All-Day-row click (or all-day drop): the
  // modal was opened at a day with no specific time, so start === end (midnight).
  // Timed clicks pass the resolved slot's distinct start/end; event-click edits
  // pass the appointment's distinct times.
  const isDateLevelOpen = useMemo(
    () => !!(selectedEvent?.start && selectedEvent?.end
      && selectedEvent.start.getTime() === selectedEvent.end.getTime()),
    [selectedEvent],
  );

  // Fallback fetch for dates outside the cached range
  useEffect(() => {
    if (!open || !selectedDate) return;
    if (isDateInCache(selectedDate)) return;

    const dayStart = new Date(`${selectedDate}T00:00:00`);
    dayStart.setHours(dayStart.getHours() - 4);
    const dayEnd = new Date(`${selectedDate}T23:59:59`);
    dayEnd.setHours(dayEnd.getHours() + 4);

    fetchLocalSlots(dayStart, dayEnd).catch(() => {
      showNotification('Failed to load slots for the selected date', 'error');
    });
  }, [open, selectedDate, isDateInCache, fetchLocalSlots, showNotification]);

  const hasChanged = useMemo(() => {
    if (!isEditing) return true;
    if (!selectedEvent?.resource) return false;

    const res = selectedEvent.resource;

    // Ground truth = the ORIGINAL times from the DB (slot_start / slot_end on the
    // resource). We must NOT use selectedEvent.start/end here because for all-day
    // drops we pre-fill those with new proposed times before opening the modal,
    // making them equal to the form state → hasChanged would never be true.
    const originalStart = res.slot_start
      ? formatDateToDateTimeLocal(parseSlotAsWallClock(res.slot_start) ?? new Date(res.slot_start))
      : '';
    const originalEnd = res.slot_end
      ? formatDateToDateTimeLocal(parseSlotAsWallClock(res.slot_end) ?? new Date(res.slot_end))
      : '';

    const currentServices = services ? [services] : [];

    // reconstruct to string for comparison
    const currentStart = selectedDate && startTime
      ? formatDateToDateTimeLocal(buildSlotRangeFromDateAndTimes(selectedDate, startTime, endTime || startTime).start)
      : '';
    const currentEnd = selectedDate && endTime
      ? formatDateToDateTimeLocal(buildSlotRangeFromDateAndTimes(selectedDate, startTime || endTime, endTime).end)
      : '';

    return (
      currentStart !== originalStart ||
      currentEnd !== originalEnd ||
      notes !== (res.notes || '') ||
      JSON.stringify(currentServices) !== JSON.stringify(res.services || [])
    );
  }, [isEditing, selectedEvent, selectedDate, startTime, endTime, notes, services]);

  const searchLeads = async (query: string) => {
    lastSearchQueryRef.current = query;
    setSearchingLeads(true);
    try {
      const res = await leadsApi.getLeads({
        search: query || undefined,
        smbId,
        limit: 50,
      });
      const mappedLeads = (res.leads || []).map((l: any) => ({
        ...l,
        full_name: l.name || l.full_name || 'Unknown',
        status: l.lead_status
      }));
      setLeads(mappedLeads);
    } catch (e) {
      console.error('Failed to load leads list:', e);
    } finally {
      setSearchingLeads(false);
    }
  };

  const handleOpenInlineCreate = useCallback(() => {
    // Save currently selected lead so we can restore it on cancel
    prevSelectedLeadRef.current = selectedLead;
    // Start with empty fields — do not prefill from search text
    setNewLeadName('');
    setNewLeadPhone('');
    setIsCreatingInline(true);
  }, [selectedLead]);

  const handleCancelInlineCreate = useCallback(() => {
    setIsCreatingInline(false);
    setNewLeadName('');
    setNewLeadPhone('');
    setNewLeadNotes('');
    setNewLeadCustomFields({});
    setPhoneError(null);
    setNameError(null);
    setInlineServerError(null);
    setNewLeadStatus('New');
    // Restore the previously selected lead into the list so Autocomplete can find it
    const prev = prevSelectedLeadRef.current;
    if (prev) {
      setLeads(existing => {
        const alreadyPresent = existing.some(l => l.id === prev.id);
        return alreadyPresent ? existing : [prev, ...existing];
      });
    }
    prevSelectedLeadRef.current = null;
  }, []);

  const handleCreateLead = async () => {
    const result = await createLead(
      newLeadName, 
      newLeadPhone, 
      LeadSource.DIRECT, 
      newLeadStatus, 
      newLeadNotes, 
      newLeadCustomFields
    );
    if (result && result.status === 'conflict' && result.existingLead) {
      openEditLead(
        result.existingLead.id,
        result.existingLead.name,
        result.existingLead.phone,
        result.existingLead.source,
        result.existingLead.status,
        'conflict',
        result.inputName,
        result.existingLead.notes,
        result.existingLead.custom_fields,
        result.inputNotes,
        result.inputCustomFields,
        result.inputStatus
      );
    }
  };

  // Memoized custom PaperComponent: avoid recreating on every render
  const CustomPaper = useMemo(() => {
    const Comp = ({ children, ...rest }: React.ComponentProps<typeof Paper>) => (
      <Paper {...rest}>
        {children}
        <Divider />
        <Box
          onMouseDown={(e) => {
            // Prevent autocomplete from losing focus / closing before click fires
            e.preventDefault();
          }}
          onClick={handleOpenInlineCreate}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 2,
            py: 1.25,
            cursor: 'pointer',
            color: 'primary.main',
            fontWeight: 600,
            fontSize: '0.875rem',
            '&:hover': { bgcolor: 'action.hover' },
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <AddIcon fontSize="small" />
          Create New Lead
        </Box>
      </Paper>
    );
    Comp.displayName = 'CreateLeadPaper';
    return Comp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOpenInlineCreate]);

  const executeCancel = async () => {
    if (!isEditing || !selectedEvent?.resource) return;
    if (isPastAppointment) {
      showNotification('Past appointments cannot be cancelled.', 'warning');
      setConfirmConfig(null);
      return;
    }

    setLoading(true);
    try {
      const success = await onSaveUpdate(selectedEvent.resource.id, { status: 'CANCELLED' });
      if (success) onClose();
    } finally {
      setLoading(false);
      setConfirmConfig(null);
    }
  };

  const handleCancelAppointment = () => {
    setConfirmConfig({ open: true, type: 'CANCEL' });
  };

  const executeSave = async () => {
    if (!selectedDate || !startTime || !endTime) return;
    if (isEditing && isPastAppointment) {
      showNotification('Past appointments cannot be rescheduled.', 'warning');
      setConfirmConfig(null);
      return;
    }

    // Get the actual slot using the timestamp value
    const selectedSlot = slotByStartTime.get(startTime);
    if (!selectedSlot) {
      showNotification('Invalid slot selected.', 'warning');
      setLoading(false);
      return;
    }

    const sDate = selectedSlot.start;
    const eDate = selectedSlot.end;
    const startStr = formatDateToDateTimeLocal(sDate);
    const endStr = formatDateToDateTimeLocal(eDate);

    if (!daySlotLookup.has(getGeneratedSlotKey(sDate, eDate))) {
      showNotification('This slot is not available for booking.', 'warning');
      setLoading(false);
      return;
    }

    setLoading(true);
    let success = false;

    try {
      if (isEditing && selectedEvent.resource) {
        // Update
        const payload: UpdateAppointmentPayload = {
          slot_start: formatISOWithTimezone(startStr, timezone),
          slot_end: formatISOWithTimezone(endStr, timezone)
        };
        success = await onSaveUpdate(selectedEvent.resource.id, payload);
      } else {
        // Create
        if (!leadId) {
          setLoading(false);
          return; // need validation
        }

        const payload: CreateAppointmentPayload = {
          smb_id: smbId,
          lead_id: leadId,
          slot_start: formatISOWithTimezone(startStr, timezone),
          slot_end: formatISOWithTimezone(endStr, timezone),
          metadata: {},
          services: services ? [services] : [],
          notes,
          source: source,
        };
        success = await onSaveCreate(payload);
      }

      if (success) {
        onClose();
      }
    } finally {
      setLoading(false);
      setConfirmConfig(null);
    }
  };

  const handleSaveWrapper = () => {
    if (isEditing) setConfirmConfig({ open: true, type: 'UPDATE' });
    else executeSave();
  };

  const getSlotDate = useCallback((slot: GeneratedSlot) => formatDateToDateTimeLocal(slot.start).split('T')[0], []);

  const getSlotDateGroup = useCallback((slot: GeneratedSlot, date: string) => {
    const slotDate = getSlotDate(slot);
    if (slotDate < date) return 0;
    if (slotDate === date) return 1;
    return 2;
  }, [getSlotDate]);

  const isSlotAvailableForDate = useCallback((slot: GeneratedSlot, date: string) => {
    const selectedDayStart = new Date(`${date}T00:00:00`);
    const selectedDayEnd = new Date(`${date}T23:59:59.999`);
    const startDate = getSlotDate(slot);

    return startDate === date || (slot.start < selectedDayStart && slot.end > selectedDayStart && slot.start <= selectedDayEnd);
  }, [getSlotDate]);

  // Valid time options come from cached slots (CalendarView's pre-fetched range)
  // or local fallback slots (if selectedDate is outside cache).
  const { validStartTimes, validEndTimes, slotByStartTime, daySlotLookup } = useMemo(() => {
    const starts: StartTimeOption[] = [];
    const ends: { time: string; booked: boolean; isPast: boolean }[] = [];
    const byStart = new Map<string, GeneratedSlot>();

    if (!selectedDate) {
      return { validStartTimes: starts, validEndTimes: ends, slotByStartTime: byStart, daySlotLookup: new Set<string>() };
    }

    const now = getNow ? getNow() : new Date();

    // Try cached slots first (includes overnight handling)
    let slotsForDay = getSlotsForDate(selectedDate).filter((slot) => isSlotAvailableForDate(slot, selectedDate));

    // If no cached slots for this date, use local fallback slots
    if (slotsForDay.length === 0) {
      slotsForDay = localSlots.filter((slot) => isSlotAvailableForDate(slot, selectedDate));
    }

    // When directly editing an existing appointment, ensure its current slot is included
    // even if the backend doesn't return it as available (because it's booked).
    // Date-level reschedules use start === end as a placeholder, so let them
    // auto-select the first real available slot for the dropped day instead.
    if (isEditing && !isDateLevelOpen && selectedEvent?.start && selectedEvent?.end) {
      const appointmentTimestamp = selectedEvent.start.getTime().toString();
      const alreadyInSlots = slotsForDay.some(slot => slot.start.getTime().toString() === appointmentTimestamp);

      if (!alreadyInSlots) {
        slotsForDay = [...slotsForDay, {
          start: selectedEvent.start,
          end: selectedEvent.end,
        }];
      }
    }

    slotsForDay = [...slotsForDay].sort((a, b) => {
      const dateGroupDiff = getSlotDateGroup(a, selectedDate) - getSlotDateGroup(b, selectedDate);
      return dateGroupDiff || a.start.getTime() - b.start.getTime();
    });

    slotsForDay.forEach((slot) => {
      const startTimeString = `${String(slot.start.getHours()).padStart(2, '0')}:${String(slot.start.getMinutes()).padStart(2, '0')}`;
      const endTimeString = `${String(slot.end.getHours()).padStart(2, '0')}:${String(slot.end.getMinutes()).padStart(2, '0')}`;
      const isPast = slot.start < now;
      const isPreviousDay = getSlotDate(slot) < selectedDate;

      // Use full timestamp as unique value to avoid collisions between overnight and regular slots
      const uniqueValue = slot.start.getTime().toString();

      starts.push({ time: startTimeString, value: uniqueValue, booked: false, isPast, isPreviousDay });
      ends.push({ time: endTimeString, booked: false, isPast });
      byStart.set(uniqueValue, slot);
    });

    return {
      validStartTimes: starts,
      validEndTimes: ends,
      slotByStartTime: byStart,
      daySlotLookup: buildGeneratedSlotLookup(slotsForDay),
    };
  }, [getSlotsForDate, isDateInCache, localSlots, getNow, selectedDate, isEditing, isDateLevelOpen, selectedEvent, getSlotDate, getSlotDateGroup, isSlotAvailableForDate]);

  // For date-level opens (Month / All-Day) there's no preset time — once the
  // day's API slots load, default the start to the earliest bookable (future)
  // slot so the user can book immediately. Overnight/12 AM slots are valid
  // defaults; their end comes from the slot itself.
  useEffect(() => {
    if (!open || !isDateLevelOpen || validStartTimes.length === 0) return;

    const currentValid = validStartTimes.some(o => o.value === startTime && !o.isPast && !o.booked);

    // Resolve which start slot we're on: keep the current one if it's already a
    // valid (future, unbooked) slot, otherwise fall back to the first bookable slot.
    let chosenValue = startTime;
    if (!currentValid) {
      const first = validStartTimes.find(o => !o.isPast && !o.booked);
      if (!first) return; // no bookable slots that day → leave Book disabled
      chosenValue = first.value;
      setStartTime(first.value);
    }

    // Always sync the end time from the resolved slot. The date-level placeholder
    // seeds endTime to "00:00", and a midnight (12:00 AM) first slot's value equals
    // that placeholder start — so without this the currentValid early-return left
    // the End Time box stuck showing 12:00 AM instead of the slot's real end.
    const slot = slotByStartTime.get(chosenValue);
    if (slot) {
      setEndTime(`${String(slot.end.getHours()).padStart(2, '0')}:${String(slot.end.getMinutes()).padStart(2, '0')}`);
    }
  }, [open, isDateLevelOpen, validStartTimes, slotByStartTime, startTime]);

  // Selecting a start time fills the end from the chosen slot's explicit end —
  // slot durations vary, so we never compute it from a fixed config duration.
  const handleStartTimeChange = (newStartTime: string) => {
    setStartTime(newStartTime);

    const slot = slotByStartTime.get(newStartTime);
    if (slot) {
      const eh = String(slot.end.getHours()).padStart(2, '0');
      const em = String(slot.end.getMinutes()).padStart(2, '0');
      setEndTime(`${eh}:${em}`);
    }
  };

  // End time is display-only (driven by the selected slot); kept for the Select contract.
  const handleEndTimeChange = (newEndTime: string) => {
    setEndTime(newEndTime);
  };

  // Format 24h to 12h
  const format12Hour = (time24: string) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const formatStartTimeOption = (opt: StartTimeOption) => {
    const labels = [
      opt.isPreviousDay ? 'Previous day' : '',
      opt.booked ? 'Booked' : '',
      opt.isPast ? 'Past' : '',
    ].filter(Boolean);

    return labels.length ? `${format12Hour(opt.time)} (${labels.join(', ')})` : format12Hour(opt.time);
  };

  const isSelectedStartTimeValid = useMemo(() => {
    if (isEditing && !isFormEditable) return true; // Just viewing
    const opt = validStartTimes.find(o => o.value === startTime);
    return opt ? !opt.booked && !opt.isPast : false;
  }, [validStartTimes, startTime, isEditing, isFormEditable]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {isEditing
            ? (isPastAppointment ? 'Appointment Details (Past)' : isFormEditable ? 'Edit Appointment' : 'Appointment Details')
            : 'Book Appointment'}
        </span>
        <Box>
          {isEditing && (
            <IconButton
              onClick={() => setIsFormEditable(!isFormEditable)}
              color={isFormEditable ? "primary" : "default"}
              title={isPastAppointment ? 'Past appointments cannot be edited' : 'Toggle Edit Mode'}
              disabled={loading || isPastAppointment}
            >
              <EditIcon />
            </IconButton>
          )}
          {isEditing && (
            <IconButton
              onClick={handleCancelAppointment}
              color="error"
              title={isPastAppointment ? 'Past appointments cannot be cancelled' : 'Cancel Appointment'}
              disabled={loading || isPastAppointment}
            >
              <DeleteIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose} disabled={loading} title="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {!isEditing && !isCreatingInline && (
            <Grid item xs={12}>
              <Autocomplete
                options={leads}
                getOptionLabel={(option) => option.full_name ? `${option.full_name} (${option.phone || 'No phone'})` : option.phone || 'Unknown'}
                loading={searchingLeads}
                onOpen={() => {
                  if (lastSearchQueryRef.current !== '') {
                    searchLeads('');
                  }
                }}
                onInputChange={(_, val, reason) => {
                  leadSearchInputRef.current = val;
                  if (reason === 'input') {
                    if (val.length >= 2) {
                      searchLeads(val);
                    } else if (val.length === 0) {
                      searchLeads(''); // Show initial list when cleared
                    }
                  } else if (reason === 'clear') {
                    searchLeads('');
                  }
                }}
                filterOptions={(x) => x} // Disable client-side filtering
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={(_, val) => {
                  setSelectedLead(val);
                  setLeadId(val ? val.id : null);
                }}
                value={selectedLead}
                PaperComponent={CustomPaper}
                ListboxProps={{ style: { maxHeight: 250, overflow: 'auto' } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search & Select Lead"
                    required
                    placeholder="Search Lead by name or phone number"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {searchingLeads ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}

          {!isEditing && isCreatingInline && (
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} color="primary">
                  Create New Lead
                </Typography>
                {inlineServerError && (
                  <Alert severity="error" sx={{ mb: 1 }} onClose={() => setInlineServerError(null)}>
                    {inlineServerError}
                  </Alert>
                )}
                <TextField
                  label="Lead Name"
                  placeholder="Enter lead name"
                  fullWidth
                  value={newLeadName}
                  onChange={(e) => {
                    setNewLeadName(e.target.value);
                    if (nameError) setNameError(null);
                    setInlineServerError(null);
                  }}
                  error={!!nameError}
                  helperText={nameError}
                  disabled={isCreatingLeadLoading}
                  size="small"
                />
                <MuiTelInput
                  label="Phone Number"
                  fullWidth
                  required
                  value={newLeadPhone}
                  onChange={(newPhone) => {
                    setNewLeadPhone(newPhone);
                    if (phoneError) setPhoneError(null);
                    setInlineServerError(null);
                  }}
                  defaultCountry="IN"
                  forceCallingCode
                  disableFormatting
                  error={!!phoneError}
                  helperText={phoneError}
                  disabled={isCreatingLeadLoading}
                  size="small"
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 300 }
                    },
                    disablePortal: true
                  }}
                />
                <LeadStatusSelect
                  smbId={smbId}
                  value={newLeadStatus}
                  onChange={(newStatus) => setNewLeadStatus(newStatus)}
                  size="small"
                />
                <DynamicFormFields
                  customColumns={customColumns}
                  notes={newLeadNotes}
                  customFields={newLeadCustomFields}
                  onNotesChange={(notesVal) => setNewLeadNotes(notesVal)}
                  onCustomFieldChange={(key, val) =>
                    setNewLeadCustomFields((prev) => ({ ...prev, [key]: val }))
                  }
                  onAddOption={addOptionToColumn}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelInlineCreate}
                    disabled={isCreatingLeadLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreateLead}
                    disabled={isCreatingLeadLoading || !newLeadPhone.trim()}
                    startIcon={isCreatingLeadLoading ? <CircularProgress size={14} color="inherit" /> : null}
                  >
                    {isCreatingLeadLoading ? 'Creating...' : 'Create Lead'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          )}

          {isEditing && selectedEvent?.resource && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary">Customer Name</Typography>
                <Typography variant="subtitle1">{selectedEvent.resource.lead_name || 'Unknown'}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Phone Number</Typography>
                <Typography variant="subtitle1">{selectedEvent.resource.lead_phone || 'Unknown'}</Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <NativePickerField
              label="Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
              disabled={isEditing && !isFormEditable}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Start Time</InputLabel>
              <Select
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                label="Start Time"
                disabled={isEditing && !isFormEditable}
                renderValue={(selected) => {
                  // Find the selected option to display its time in 12-hour format
                  const selectedOpt = validStartTimes.find(opt => opt.value === selected);
                  return selectedOpt ? formatStartTimeOption(selectedOpt) : '';
                }}
              >
                {validStartTimes.map(opt => (
                  <MenuItem key={`start-${opt.value}`} value={opt.value} disabled={opt.booked || opt.isPast}>
                    {formatStartTimeOption(opt)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>End Time</InputLabel>
              <Select
                value={endTime}
                onChange={(e) => handleEndTimeChange(e.target.value)}
                label="End Time"
                disabled={true}
                renderValue={(selected) => format12Hour(selected as string)}
              >
                {validEndTimes.map(opt => (
                  <MenuItem key={`end-${opt.time}`} value={opt.time} disabled={opt.booked || opt.isPast}>
                    {format12Hour(opt.time)} {opt.booked ? '(Booked)' : opt.isPast ? '(Past)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Service Name"
              placeholder="e.g. Dental Checkup"
              value={services}
              onChange={(e) => setServices(e.target.value)}
              disabled={isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes"
              multiline
              fullWidth
              value={notes}
              disabled={isEditing}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          onClick={handleSaveWrapper}
          disabled={loading || (isEditing && !isFormEditable) || (!isEditing && !leadId) || !selectedDate || !startTime || !endTime || (isEditing && !hasChanged) || (!isEditing && !isSelectedStartTimeValid) || (isEditing && isFormEditable && !isSelectedStartTimeValid)}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isEditing ? 'Update Appointment' : 'Book Appointment'}
        </Button>
      </DialogActions>

      {/* Confirmation Dialog */}
      {confirmConfig && (
        <Dialog open={confirmConfig.open} onClose={() => setConfirmConfig(null)} maxWidth="xs" fullWidth>
          <DialogTitle>
            {confirmConfig.type === 'UPDATE' ? 'Confirm Update' : 'Confirm Cancellation'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {confirmConfig.type === 'UPDATE'
                ? 'Are you sure you want to update this appointment?'
                : 'Are you sure you want to cancel this appointment?'}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button onClick={() => setConfirmConfig(null)} color="inherit" disabled={loading}>Back</Button>
            <Button
              onClick={confirmConfig.type === 'UPDATE' ? executeSave : executeCancel}
              color={confirmConfig.type === 'UPDATE' ? 'primary' : 'error'}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {confirmConfig.type === 'UPDATE' ? 'Confirm' : 'Cancel Appointment'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {renderEditLeadDialog()}
    </Dialog>
  );
};
